import { Injectable } from '@angular/core';
import Dexie from 'dexie';
import cuid from 'cuid';
import { BehaviorSubject, combineLatest, from, ReplaySubject } from 'rxjs';
import {
  first,
  map,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { ImageMetadataStatic } from 'ZalamoLibrary/src/lib/core/image/image-metadata.service';
import { Dimensions } from 'ZalamoLibrary/src/lib/core/image/resizer.service';
import { PrintsPricingListForDisplay } from '@ui/suppliers/graphql';
import { select, Store } from '@ngrx/store';
import { selectCurrentCurrency } from 'ZalamoLibrary/src/lib/zalamo/currencies/redux/currency.selectors';
import { setOwnPhotosPrintsPricing } from '@ui/products/shopping-cart-back/actions/actions';
import { selectOwnPhotosCost } from '@ui/products/shopping-cart-back/actions/selector';
import { ZalamoDialogService } from '@ui/zalamo-dialog/zalamo-dialog.service';
import { PickFormatForOwnPhotosComponent } from '@ui/print-own-photos/pick-format-for-own-photos/pick-format-for-own-photos.component';
import { dump } from 'ZalamoLibrary/src/lib/core/exif';
import { insert } from 'ZalamoLibrary/src/lib/core/exif/insert';
import {
  NativeOwnPhoto,
  NativeOwnPhotosBridgeService,
} from '@ui/print-own-photos/native-own-photos-bridge.service';

const reduce = require('image-blob-reduce');
// @ts-ignore
const picaInstance = typeof window.pica === 'function' ? pica() : null;
export const database = new Dexie('privatePrints');
database.version(6).stores({
  privatePrint: `&id`,
});

export type OwnPhotoThumbnail =
  | Blob
  | {
      data: Blob;
      width?: number;
      height?: number;
    };

export type OwnPhotoBasketItem = {
  id: string;
  file?: File | null;
  context: string;
  uploaded: boolean;
  count: number;
  ordered: boolean;
  width: number;
  height: number;
  printFormatId?: string | null;
  thumb?: OwnPhotoThumbnail | null;
  crop: number[];
  nativePhotoId?: string | null;
  originalName?: string | null;
  size?: number | null;
  thumbnailDataUrl?: string | null;
};

@Injectable({
  providedIn: 'root',
})
export class PrintOwnPhotosBasketService {
  get context(): string | null {
    return this._context;
  }

  set context(value: string | null) {
    this._context = `${value || ''}`.trim() || null;
    this.basketUpdated.next(undefined);
    void this.syncNativeBasket();
    void this.calculateCurrentTotal();
  }

  pricing = new BehaviorSubject<
    Omit<PrintsPricingListForDisplay, 'printsPricingListForDisplayId'>
  >(null);

  table = database.table<OwnPhotoBasketItem>('privatePrint');
  basketUpdated = new ReplaySubject(1);
  basket$ = this.basketUpdated.pipe(
    switchMap(() =>
      from(
        this.table
          .filter((v) => v.context === this._context && !v.ordered)
          .toArray(),
      ),
    ),
  );
  basketSize$ = this.basketUpdated.pipe(
    switchMap(() =>
      from(
        this.table
          .filter((v) => v.context === this._context && !v.ordered)
          .count(),
      ),
    ),
    startWith(0),
    shareReplay(1),
  );

  private _context: string | null = null;
  readonly nativePickerAvailable$ =
    this.nativeOwnPhotosBridgeService.available$;

  cost$ = combineLatest([
    this.store.pipe(select(selectCurrentCurrency)),
    this.store.pipe(select(selectOwnPhotosCost)),
  ]).pipe(
    map(
      ([currency, cost]) =>
        cost.afterDiscount.find((v) => v.code === currency).price,
    ),
  );

  constructor(
    private store: Store,
    private zalamoDialogService: ZalamoDialogService,
    private nativeOwnPhotosBridgeService: NativeOwnPhotosBridgeService,
  ) {
    this.basketUpdated.next(undefined);
  }

  getFile(id: string) {
    return this.table.get(id);
  }

  updateThumb(thumb: OwnPhotoThumbnail, id: string) {
    return this.table.update(id, { thumb });
  }

  async updateCrop(crop: number[], id: string) {
    await this.table.update(id, { crop });
    const item = await this.table.get(id);
    await this.persistNativePhotoSettings(item);
  }

  async updateCount(count: number, { id }: { id: string }) {
    await this.table.update(id, { count });
    const item = await this.table.get(id);
    await this.persistNativePhotoSettings(item);
    await this.calculateCurrentTotal();
  }

  async updateFormat(printFormatId: string) {
    await this.table
      .filter((v) => v.context === this._context)
      .modify({
        printFormatId,
        crop: [],
      });
    await Promise.all(
      (
        await this.table
          .filter((v) => v.context === this._context && !!v.nativePhotoId)
          .toArray()
      ).map((item) => this.persistNativePhotoSettings(item)),
    );
    await this.calculateCurrentTotal();
  }

  async addFile(file: File) {
    if (!this._context) {
      return null;
    }

    const allElements = await this.table
      .filter((v) => v.context === this._context && !v.ordered)
      .toArray();

    if (!allElements.length || !localStorage.lastOwnPhotosFormatId) {
      await this.pickDefaultPrintFormat();
    }
    if (!allElements.some((element) => element.file?.name === file.name)) {
      const meta = await ImageMetadataStatic.getDetails(file);
      const dimensions: Dimensions = {
        width: (meta.file
          ? meta.file['Image Width']
          : meta.pngFile['Image Width']
        ).value,
        height: (meta.file
          ? meta.file['Image Height']
          : meta.pngFile['Image Height']
        ).value,
      };
      const exif = {
        // @ts-ignore
        Exif: { [meta.exif.Orientation.id]: meta.exif.Orientation.value },
      };
      // @ts-ignore

      // @ts-ignore
      const thumb = meta?.Thumbnail?.image
        ? // @ts-ignore
          await fetch(
            insert(
              dump(exif),
              // @ts-ignore
              `data:image/jpeg;base64,${meta?.Thumbnail?.base64}`,
            ),
          ).then((v) => v.blob())
        : await reduce
            .default({ pica: picaInstance })
            .toBlob(file, { max: 300 });

      await this.table.add({
        id: cuid(),
        file,
        nativePhotoId: null,
        uploaded: false,
        context: this._context,
        count: 1,
        ordered: false,
        thumb,
        height: dimensions.height,
        width: dimensions.width,
        crop: [],
        originalName: file.name,
        size: file.size,
        thumbnailDataUrl: null,
        printFormatId:
          allElements[0]?.printFormatId ?? localStorage.lastOwnPhotosFormatId,
      });
      this.basketUpdated.next(undefined);
      await this.calculateCurrentTotal();
      return thumb;
    }
  }

  async pickNativePhotos() {
    if (!this._context) {
      return [];
    }

    const allElements = await this.table
      .filter((v) => v.context === this._context && !v.ordered)
      .toArray();

    if (!allElements.length || !localStorage.lastOwnPhotosFormatId) {
      await this.pickDefaultPrintFormat();
    }

    const printFormatId =
      allElements[0]?.printFormatId ?? localStorage.lastOwnPhotosFormatId;

    const photos = await this.nativeOwnPhotosBridgeService.pickOwnPhotos({
      context: this._context,
      printFormatId,
    });

    await this.upsertNativePhotos(photos);
    return photos;
  }

  async calculateCurrentTotal() {
    if (!this.pricing.value?.list?.length) {
      return;
    }

    await combineLatest([
      this.basket$,
      this.store.pipe(select(selectCurrentCurrency)),
    ])
      .pipe(
        tap(([basket, currentCurrency]) => {
          const total = basket.reduce((acc, curr) => {
            const price = this.pricing.value.list.find(
              (v) => v.printFormatId === curr.printFormatId,
            )?.price;
            if (!price) {
              return acc;
            }

            const priceInCurrency = price.find(
              (v) => v.code === currentCurrency,
            );
            if (!priceInCurrency) {
              return acc;
            }

            return acc + priceInCurrency.price * curr.count;
          }, 0);
          this.store.dispatch(
            setOwnPhotosPrintsPricing({
              pricing: {
                beforeDiscount: [
                  {
                    price: total,
                    code: currentCurrency,
                  },
                ],
                afterDiscount: [
                  {
                    price: total,
                    code: currentCurrency,
                  },
                ],
              },
            }),
          );
        }),
        first(),
      )
      .subscribe();
  }

  async delete({ id }: { id: string }) {
    const item = await this.table.get(id);
    if (item?.nativePhotoId && item.context) {
      await this.nativeOwnPhotosBridgeService.deleteOwnPhoto({
        context: item.context,
        id: item.nativePhotoId,
      });
    }

    await this.table.delete(id);
    this.basketUpdated.next(undefined);
    await this.calculateCurrentTotal();
  }

  async placeOrder() {
    const ownPhotos = await this.table
      .filter((v) => v.context === this._context && !v.ordered)
      .toArray();
    await Promise.all(
      ownPhotos
        .filter((photo) => !!photo.nativePhotoId)
        .map((photo) =>
          this.nativeOwnPhotosBridgeService
            .updateOwnPhoto({
              context: photo.context,
              id: photo.nativePhotoId,
              count: photo.count,
              ordered: true,
              printFormatId: photo.printFormatId || null,
              crop: photo.crop || [],
            })
            .catch(() => null),
        ),
    );
    await this.table
      .filter((v) => v.context === this._context)
      .modify({
        ordered: true,
      });
  }

  updateTotal() {}

  async setPricing(
    pricing: Omit<PrintsPricingListForDisplay, 'printsPricingListForDisplayId'>,
  ) {
    this.pricing.next(pricing);
    await this.calculateCurrentTotal();
  }

  getOriginalName(photo: OwnPhotoBasketItem) {
    return `${photo.originalName || photo.file?.name || ''}`.trim();
  }

  getSize(photo: OwnPhotoBasketItem) {
    return Number(photo.size || photo.file?.size || 0);
  }

  async getNativeThumbnailDataUrl(photo: OwnPhotoBasketItem) {
    if (photo.thumbnailDataUrl) {
      return photo.thumbnailDataUrl;
    }

    if (!photo.nativePhotoId || !photo.context) {
      return null;
    }

    const thumbnail =
      await this.nativeOwnPhotosBridgeService.getOwnPhotoThumbnail({
        context: photo.context,
        id: photo.nativePhotoId,
      });

    if (thumbnail.thumbnailDataUrl) {
      await this.table.update(photo.id, {
        thumbnailDataUrl: thumbnail.thumbnailDataUrl,
      });
    }

    return thumbnail.thumbnailDataUrl;
  }

  private async pickDefaultPrintFormat() {
    await new Promise<void>((resolve) => {
      const ref = this.zalamoDialogService.openFull(
        PickFormatForOwnPhotosComponent,
        {
          isBare: true,
          closable: false,
        },
      );
      ref.componentInstance.picked
        .pipe(
          tap((v) => (localStorage.lastOwnPhotosFormatId = v)),
          tap(() => ref.close()),
          takeUntil(ref.afterClosed()),
        )
        .subscribe(() => resolve());
    });
  }

  private async syncNativeBasket() {
    if (!this._context) {
      return;
    }

    const available =
      this.nativeOwnPhotosBridgeService.isAvailable() ||
      (await this.nativeOwnPhotosBridgeService.waitForAvailability(1000));
    if (!available) {
      return;
    }

    const photos = await this.nativeOwnPhotosBridgeService.listOwnPhotos({
      context: this._context,
    });
    await this.upsertNativePhotos(photos);

    const nativePhotoIds = new Set(photos.map((photo) => photo.id));
    const staleNativeRows = await this.table
      .filter(
        (row) =>
          row.context === this._context &&
          !!row.nativePhotoId &&
          !nativePhotoIds.has(row.nativePhotoId),
      )
      .toArray();

    await Promise.all(staleNativeRows.map((row) => this.table.delete(row.id)));
    this.basketUpdated.next(undefined);
    await this.calculateCurrentTotal();
  }

  private async upsertNativePhotos(photos: NativeOwnPhoto[]) {
    if (!photos.length) {
      return;
    }

    for (const photo of photos) {
      const existing = await this.table
        .filter(
          (row) =>
            row.context === photo.context && row.nativePhotoId === photo.id,
        )
        .first();
      const item: OwnPhotoBasketItem = {
        id: existing?.id || photo.id,
        file: null,
        nativePhotoId: photo.id,
        uploaded: false,
        context: photo.context,
        count: photo.count,
        ordered: existing?.ordered ?? photo.ordered,
        thumb: existing?.thumb || null,
        height: photo.height,
        width: photo.width,
        crop: photo.crop,
        originalName: photo.originalName,
        size: photo.size,
        thumbnailDataUrl: photo.thumbnailDataUrl || existing?.thumbnailDataUrl,
        printFormatId:
          photo.printFormatId ||
          existing?.printFormatId ||
          localStorage.lastOwnPhotosFormatId ||
          null,
      };

      if (existing) {
        await this.table.put(item);
      } else {
        await this.table.add(item);
      }
    }

    this.basketUpdated.next(undefined);
    await this.calculateCurrentTotal();
  }

  private persistNativePhotoSettings(item: OwnPhotoBasketItem | undefined) {
    if (!item?.nativePhotoId || !item.context) {
      return Promise.resolve(null);
    }

    return this.nativeOwnPhotosBridgeService.updateOwnPhoto({
      context: item.context,
      id: item.nativePhotoId,
      count: item.count,
      ordered: item.ordered,
      printFormatId: item.printFormatId || null,
      crop: item.crop || [],
    });
  }
}
